import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import moment from 'moment';
import MemberPanelDialog from './MemberPanelDialog';
import { useMemberConversations } from '../../../../queries/useCapability';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

export interface MemberConversation {
  id: string;
  title: string;
  /** Unix seconds of the last message. */
  updatedAt: number;
  /** The member's questions and Elliott's responses, in order. */
  messages: Array<{ role: 'member' | 'elliott'; text: string }>;
}

const Conversation: React.FC<{ conversation: MemberConversation; memberName: string }> = ({
  conversation,
  memberName
}) => {
  const [open, setOpen] = useState(false);
  const questions = conversation.messages.filter((m) => m.role === 'member').length;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
      {/* The same row as a capability's title on the Capability panel. */}
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          border: '1px solid rgba(247, 148, 32, 0.45)',
          bgcolor: 'rgba(247, 148, 32, 0.06)',
          borderRadius: '4px',
          pl: '12px',
          pr: '10px',
          py: '2px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: 36,
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'rgba(247, 148, 32, 0.12)' },
          // A phone: the lesson's whole title beside the chevron, how many and when on the line beneath.
          [MOBILE_SX]: {
            flexWrap: 'wrap',
            rowGap: '2px',
            py: '8px',
            '& > .conversation-meta': { order: 1, flexBasis: '100%' }
          }
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            textTransform: 'uppercase',
            color: '#FDFDFD',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [MOBILE_SX]: { whiteSpace: 'normal', flexBasis: 'calc(100% - 30px)' }
          }}
        >
          {conversation.title}
        </Typography>
        <Typography
          className="conversation-meta"
          sx={{ flexShrink: 0, fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#B6B6B6', whiteSpace: 'nowrap' }}
        >
          {questions} {questions === 1 ? 'Question' : 'Questions'} · {moment.unix(conversation.updatedAt).fromNow()}
        </Typography>
        <Box sx={{ flexShrink: 0, width: 22, height: 22, display: 'flex', alignItems: 'center', color: '#FDFDFD' }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ border: '1px solid #2A2A2A', borderRadius: '4px', px: '12px' }}>
          {conversation.messages.map((message, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              sx={{
                py: '12px',
                // A brandy rule between a question and Elliott's response; grey before the next question.
                ...(i > 0
                  ? {
                      borderTop: message.role === 'elliott' ? '1px solid rgba(247, 148, 32, 0.45)' : '1px solid #2A2A2A'
                    }
                  : null)
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: message.role === 'elliott' ? '#F79420' : '#868686',
                  mb: '4px'
                }}
              >
                {message.role === 'elliott' ? 'Elliott' : memberName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  lineHeight: '20px',
                  color: '#FDFDFD',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {message.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

/** One member's conversations with Elliott: each opens onto their questions and Elliott's responses. */
const MemberConversationsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  /** The count on the member's row. */
  count: number;
}> = ({ open, onClose, memberId, memberName, count }) => {
  // Their questions to Elliott with Elliott's answers (GET /v3/admin/capability/members/{id}/conversations).
  const { data, isLoading, isError } = useMemberConversations(open ? memberId : null);
  const conversations = data?.conversations ?? [];
  return (
    <MemberPanelDialog open={open} onClose={onClose} width={560} title={`${memberName}'s Elliott Conversations`}>
      {isLoading ? (
        <Box aria-busy aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Array.from({ length: 4 }, (_, i) => (
            <Bone key={i} height={44} />
          ))}
        </Box>
      ) : conversations.length === 0 ? (
        <Typography
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#868686', textAlign: 'center', pt: '8px' }}
        >
          {isError
            ? `${memberName}'s conversations could not be loaded. Close this panel and try again.`
            : count > 0
            ? `${memberName}'s conversations can't be shown here yet.`
            : `${memberName} has not had a conversation with Elliott yet.`}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
          {conversations.map((c) => (
            <Conversation key={c.id} conversation={c} memberName={memberName} />
          ))}
        </Box>
      )}
    </MemberPanelDialog>
  );
};

export default MemberConversationsModal;
